import { useState, useEffect, useCallback, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import { Input } from "@shadcn/components/ui/input";
import { Button } from "@shadcn/components/ui/button";
import { Loader2, Car, Search } from "lucide-react";
import { vehicleSearchApi, VehicleResult } from "./api";
import { VehicleSearchProps } from "./VehicleSearch.types";
import { VehicleSearchResults } from "./VehicleSearchResults";
import { VehicleSearchManualEntry } from "./VehicleSearchManualEntry";

export const VehicleSearch = ({ name, required, disabled, helperText, showManualEntryButton = false }: VehicleSearchProps) => {
  const description = getFieldHelperText(helperText, required);
  const { control, setValue, watch, trigger, getValues, formState } = useFormContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasBeenClearedRef = useRef(false);

  const performSearch = useCallback(
    async (licencePlate: string) => {
      if (!licencePlate || licencePlate.trim().length === 0) {
        setError(null);
        setSelectedVehicle(null);
        setHasSearched(false);
        setManualMode(false);
        return;
      }

      setIsSearching(true);
      setError(null);
      setHasSearched(true);
      setManualMode(false);
      
      try {
        const response = await vehicleSearchApi.search({ licencePlate: licencePlate.trim().toUpperCase() });
        if (response.result) {
          setSelectedVehicle(response.result);
          setError(null);
          hasBeenClearedRef.current = false;
          
          // Populate form fields
          setValue(`${name}.licencePlate`, licencePlate.trim().toUpperCase());
          setValue(`${name}.vin`, response.result.vin);
          setValue(`${name}.make`, response.result.make);
          setValue(`${name}.model`, response.result.model);
          setValue(`${name}.cylinderCapacity`, response.result.cylinderCapacity);
          setValue(`${name}.numberOfDoors`, response.result.numberOfDoors);
          setValue(`${name}.yearOfManufacture`, response.result.yearOfManufacture);
          setValue(`${name}.fuelType`, response.result.fuelType);
          setValue(`${name}.colour`, response.result.colour);
          setValue(`${name}.taxed`, response.result.taxed);
          setValue(`${name}.mot`, response.result.mot);
          setValue(`${name}.isManualEntry`, false);
        } else {
          setSelectedVehicle(null);
          setError("No vehicle found for this registration plate. You can enter the details manually below.");
        }
      } catch (error: any) {
        console.error("Vehicle search error:", error);
        setSelectedVehicle(null);
        setError(error?.response?.data?.message || "Failed to search for vehicle. Please try again.");
      } finally {
        setIsSearching(false);
      }
    },
    [name, setValue]
  );

  const handleSearch = () => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      return;
    }
    performSearch(searchQuery);
  };

  const handleManualMode = () => {
    setManualMode(true);
    setSelectedVehicle(null);
    setError(null);
    hasBeenClearedRef.current = false;

    // Clear form fields - only keep the fields we collect
    // Set vin to "manual-entry" to match validation behavior
    setValue(`${name}.licencePlate`, searchQuery.trim().toUpperCase());
    setValue(`${name}.vin`, "manual-entry");
    setValue(`${name}.make`, "");
    setValue(`${name}.model`, "");
    setValue(`${name}.cylinderCapacity`, "");
    setValue(`${name}.numberOfDoors`, "");
    setValue(`${name}.yearOfManufacture`, "");
    setValue(`${name}.fuelType`, "");
    setValue(`${name}.colour`, "");
    setValue(`${name}.taxed`, false);
    setValue(`${name}.mot`, false);
    setValue(`${name}.isManualEntry`, true);
  };

  const handleEditResult = () => {
    if (!selectedVehicle) return;
    hasBeenClearedRef.current = false;
    setManualMode(true);
    setSelectedVehicle(null);
    setError(null);

    setValue(`${name}.licencePlate`, searchQuery.trim().toUpperCase());
    setValue(`${name}.vin`, "manual-entry");
    setValue(`${name}.make`, selectedVehicle.make);
    setValue(`${name}.model`, selectedVehicle.model);
    setValue(`${name}.cylinderCapacity`, selectedVehicle.cylinderCapacity);
    setValue(`${name}.numberOfDoors`, selectedVehicle.numberOfDoors);
    setValue(`${name}.yearOfManufacture`, selectedVehicle.yearOfManufacture);
    setValue(`${name}.fuelType`, selectedVehicle.fuelType);
    setValue(`${name}.colour`, selectedVehicle.colour);
    setValue(`${name}.taxed`, selectedVehicle.taxed);
    setValue(`${name}.mot`, selectedVehicle.mot);
    setValue(`${name}.isManualEntry`, true);
  };

  const handleSearchAgain = () => {
    hasBeenClearedRef.current = true;
    setSelectedVehicle(null);
    setError(null);
    setSearchQuery("");
    setHasSearched(false);
    setManualMode(false);
    
    // Clear form fields - use empty strings but ref prevents fallback to defaults
    setValue(`${name}.vin`, "");
    setValue(`${name}.make`, "");
    setValue(`${name}.model`, "");
    setValue(`${name}.cylinderCapacity`, "");
    setValue(`${name}.numberOfDoors`, "");
    setValue(`${name}.yearOfManufacture`, "");
    setValue(`${name}.fuelType`, "");
    setValue(`${name}.colour`, "");
    setValue(`${name}.licencePlate`, "");
    setValue(`${name}.taxed`, false);
    setValue(`${name}.mot`, false);
    setValue(`${name}.isManualEntry`, false);
  };

  // Check for existing vehicle data - prioritize current values, only use defaultValues if current is undefined
  const watchedVin = watch(`${name}.vin`);
  const watchedIsManualEntry = watch(`${name}.isManualEntry`);
  const watchedMake = watch(`${name}.make`);
  const watchedModel = watch(`${name}.model`);
  const watchedColour = watch(`${name}.colour`);
  const watchedCylinderCapacity = watch(`${name}.cylinderCapacity`);
  const watchedNumberOfDoors = watch(`${name}.numberOfDoors`);
  const watchedYearOfManufacture = watch(`${name}.yearOfManufacture`);
  const watchedFuelType = watch(`${name}.fuelType`);
  const watchedTaxed = watch(`${name}.taxed`);
  const watchedMot = watch(`${name}.mot`);
  
  // Only use defaultValues if current value is undefined (not set yet), not if it's been cleared to empty string
  const currentVin = getValues(`${name}.vin`);
  const defaultVin = formState.defaultValues?.[name]?.vin;
  const vin = hasBeenClearedRef.current
    ? (currentVin !== undefined ? currentVin : "")  // If cleared, use current or empty string, don't fall back to defaults
    : (currentVin !== undefined 
      ? currentVin 
      : (defaultVin ?? watchedVin));
  const hasExistingVehicle = !hasBeenClearedRef.current && vin && typeof vin === 'string' && vin.trim() !== "";
  
  const currentIsManualEntry = getValues(`${name}.isManualEntry`);
  const defaultIsManualEntry = formState.defaultValues?.[name]?.isManualEntry;
  const isManualEntry = currentIsManualEntry !== undefined 
    ? currentIsManualEntry 
    : (defaultIsManualEntry ?? watchedIsManualEntry);
  
  // Check if there's manual entry data (licencePlate, make, model, colour filled)
  const currentLicencePlate = getValues(`${name}.licencePlate`);
  const defaultLicencePlate = formState.defaultValues?.[name]?.licencePlate;
  const licencePlate = hasBeenClearedRef.current
    ? (currentLicencePlate !== undefined ? currentLicencePlate : "")  // If cleared, use current or empty string, don't fall back to defaults
    : (currentLicencePlate !== undefined 
      ? currentLicencePlate 
      : (defaultLicencePlate ?? watch(`${name}.licencePlate`)));
  const hasManualEntryData = !hasBeenClearedRef.current 
    && licencePlate && typeof licencePlate === 'string' && licencePlate.trim() !== "" 
    && watchedMake && typeof watchedMake === 'string' && watchedMake.trim() !== ""
    && watchedModel && typeof watchedModel === 'string' && watchedModel.trim() !== ""
    && watchedColour && typeof watchedColour === 'string' && watchedColour.trim() !== "";
  
  useEffect(() => {
    if (!manualMode && selectedVehicle === null) {
      // Check for manual entry first
      if (isManualEntry === true || (hasManualEntryData && (!vin || vin === "" || vin === "manual-entry"))) {
        setManualMode(true);
        setHasSearched(true);
        hasBeenClearedRef.current = false;
        // Ensure isManualEntry flag is set and vin is set to "manual-entry" to match validation behavior
        setValue(`${name}.isManualEntry`, true);
        if (!vin || vin === "") {
          setValue(`${name}.vin`, "manual-entry");
        }
      } else if (hasExistingVehicle) {
        // Reconstruct selectedVehicle from form data
        if (vin && vin !== "manual-entry") {
          const defaultMake = formState.defaultValues?.[name]?.make;
          const defaultModel = formState.defaultValues?.[name]?.model;
          const defaultColour = formState.defaultValues?.[name]?.colour;
          const defaultCylinderCapacity = formState.defaultValues?.[name]?.cylinderCapacity;
          const defaultNumberOfDoors = formState.defaultValues?.[name]?.numberOfDoors;
          const defaultYearOfManufacture = formState.defaultValues?.[name]?.yearOfManufacture;
          const defaultFuelType = formState.defaultValues?.[name]?.fuelType;
          const defaultTaxed = formState.defaultValues?.[name]?.taxed;
          const defaultMot = formState.defaultValues?.[name]?.mot;
          const defaultLicencePlate = formState.defaultValues?.[name]?.licencePlate;
          
          // Set searchQuery from saved licencePlate for display
          const savedLicencePlate = licencePlate || defaultLicencePlate || "";
          if (savedLicencePlate) {
            setSearchQuery(savedLicencePlate);
          }
          
          const reconstructedVehicle: VehicleResult = {
            vin: vin,
            make: watchedMake || defaultMake || "",
            model: watchedModel || defaultModel || "",
            cylinderCapacity: watchedCylinderCapacity || defaultCylinderCapacity || "",
            numberOfDoors: watchedNumberOfDoors || defaultNumberOfDoors || "",
            yearOfManufacture: watchedYearOfManufacture || defaultYearOfManufacture || "",
            fuelType: watchedFuelType || defaultFuelType || "",
            colour: watchedColour || defaultColour || "",
            taxed: watchedTaxed ?? defaultTaxed ?? false,
            mot: watchedMot ?? defaultMot ?? false,
          };
          setSelectedVehicle(reconstructedVehicle);
          setHasSearched(true);
          hasBeenClearedRef.current = false;
          // Ensure isManualEntry flag is set to false for vehicle search results
          setValue(`${name}.isManualEntry`, false);
        }
      }
    }
  }, [
    manualMode,
    selectedVehicle,
    isManualEntry,
    hasManualEntryData,
    hasExistingVehicle,
    vin,
    licencePlate,
    watchedMake,
    watchedModel,
    watchedColour,
    watchedCylinderCapacity,
    watchedNumberOfDoors,
    watchedYearOfManufacture,
    watchedFuelType,
    watchedTaxed,
    watchedMot,
    name,
    formState.defaultValues,
    setValue,
  ]);

  // Trigger validation when state changes
  useEffect(() => {
    if (required) {
      trigger(`${name}.plateSearch`);
      trigger(`${name}.vin`);
    }
  }, [selectedVehicle, manualMode, hasSearched, required, name, trigger]);

  const showVehicleDetails = (manualMode || selectedVehicle !== null || hasExistingVehicle || hasManualEntryData);
  const showManualButtonAfterFailedSearch =
    hasSearched && !selectedVehicle && !isSearching && error !== null;
  const showManualButton = showManualEntryButton || showManualButtonAfterFailedSearch;

  function validate() {
    if (hasExistingVehicle || hasManualEntryData) {
      return true;
    }
    
    if (!hasSearched && !manualMode) {
      return "Please search for a vehicle";
    }
    
    return true;
  }

  return (
    <div className="space-y-4">
      {/* Hidden field to ensure isManualEntry is included in form submission */}
      <FormField
        control={control}
        name={`${name}.isManualEntry`}
        render={() => <></>}
      />
      
      {/* Top-level validation: has/has not searched */}
      <FormField
        control={control}
        name={`${name}.plateSearch`}
        rules={
          required && { validate: validate }
        }
        render={() => (
          <FormItem>
            <FormMessage />
          </FormItem>
        )}
      />

      {!showVehicleDetails && (
        <div ref={containerRef} className="space-y-3">
          <FormItem>
            <FormControl>
              <div className="flex gap-2">
                <div className="relative max-w-[200px]">
                  <Input
                    id="vehicle-search"
                    type="text"
                    placeholder="Enter registration..."
                    value={searchQuery}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "").toUpperCase();
                      setSearchQuery(value);
                      setError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    disabled={disabled || isSearching}
                    className="pr-10"
                  />
                  {!isSearching && searchQuery && (
                    <Car className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  )}
                </div>
                <Button
                  type="button"
                  onClick={handleSearch}
                  disabled={disabled || isSearching || !searchQuery || searchQuery.trim().length === 0}
                  variant="default"
                >
                  {isSearching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Search
                    </>
                  )}
                </Button>
                {showManualEntryButton && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleManualMode}
                    disabled={disabled}
                  >
                    Enter manually
                  </Button>
                )}
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
            {error && !isSearching && (
              <p className="text-sm text-destructive mt-1">{error}</p>
            )}
          </FormItem>

          {showManualButton && !showManualEntryButton && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleManualMode}
                disabled={disabled}
              >
                Enter manually
              </Button>
            </div>
          )}
        </div>
      )}

      {showVehicleDetails && (
        <>
          {(selectedVehicle !== null || (hasExistingVehicle && isManualEntry !== true)) ? (
            <VehicleSearchResults
              disabled={disabled}
              selectedVehicle={selectedVehicle}
              searchQuery={searchQuery}
              onSearchAgain={handleSearchAgain}
              onEdit={handleEditResult}
            />
          ) : (
            <>
              <div className="flex items-center justify-end mb-4">
                <Button type="button" variant="outline" size="sm" onClick={handleSearchAgain} disabled={disabled}>
                  Search again
                </Button>
              </div>
              {manualMode && (
                <VehicleSearchManualEntry name={name} required={required} disabled={disabled} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
